"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { Button } from "./ui/button"
import { Card } from "./ui/card"
import { TripData, ItineraryDay } from "../data/tripData"
import { useLanguage } from "../hooks/useLanguage"

interface DayItinerary {
  day: number | string
  title: string
  location: string
  description: string
  activities: string[]
  media: { type: "video" | "image"; src: string; alt?: string; previewSrc?: string }[]
  highlight: string
  destinations?: string[]
  activityLinks?: string[]
  info?: string[]
}

// Function to generate destination URL from location name
const getDestinationUrl = (location: string, destinations?: string[]): string | null => {
  // If we have specific destinations, use the first one
  if (destinations && destinations.length > 0) {
    return `/destination/${destinations[0]}`
  }
  
  // Map location names to destination URLs
  const locationMap: Record<string, string> = {
    'Collalto Sabino, Italia': 'collalto_sabino',
    'Collalto Sabino, Italy': 'collalto_sabino',
    'Santo Stefano di Sessanio, Italia': 'santo_stefano_di_sessanio',
    'Santo Stefano di Sessanio, Italy': 'santo_stefano_di_sessanio',
    'Rocca Calascio, Italia': 'rocca_calascio',
    'Rocca Calascio, Italy': 'rocca_calascio',
    'Campo Imperatore, Italia': 'gran_sasso', // Campo Imperatore is in Gran Sasso park
    'Campo Imperatore, Italy': 'gran_sasso',
    'Villetta Barrea, Italia': 'villetta_barrea',
    'Villetta Barrea, Italy': 'villetta_barrea',
    'Scanno, Italia': 'villetta_barrea', // Using Villetta Barrea as closest available
    'Scanno, Italy': 'villetta_barrea',
    'Torino di Sangro, Italia': 'torino_di_sangro',
    'Torino di Sangro, Italy': 'torino_di_sangro'
  }
  
  const destinationId = locationMap[location]
  return destinationId ? `/destination/${destinationId}` : null
}

// Function to generate activity URL from activity name
const getActivityUrl = (activityName: string): string | null => {
  const activityMap: Record<string, string> = {
    'Azienda Tilli': 'cantina_tilli',
    'Tilli Company': 'cantina_tilli',
    'Wine Tasting': 'cantina_tilli',
    'Degustazione Vini': 'cantina_tilli',
    'Degustazione vini nella Azienda Tilli': 'cantina_tilli',
    'Pernottamento in Albergo Sextantio': 'sextantio',
    'Overnight at Sextantio Hotel': 'sextantio',
    'Sextantio Hotel': 'sextantio',
    'Arrosticini a Mucciante': 'arrosticini_mucciante',
    'Avvistamento cervi': 'wild_life_spotting'
  }
  
  const activityId = activityMap[activityName]
  return activityId ? `/activities/${activityId}` : null
}

interface TravelItineraryProps {
  tripData: TripData
}

const DayHeader = ({ day }: { day: number | string }) => {
  const { t } = useLanguage()
  
  const getDayDisplay = (day: number | string) => {
    if (typeof day === 'number') {
      return {
        number: day.toString().padStart(2, "0"),
        title: `${t('common.day')} ${day}`
      }
    } else if (day === "4-6") {
      return {
        number: "04 - 06",
        title: `${t('common.days')} 4, 5 ${t('common.and')} 6`
      }
    } else if (day === "4-7") {
      return {
        number: "04 - 07",
        title: `${t('common.days')} 4, 5, 6 ${t('common.and')} 7`
      }
    } else {
      return {
        number: day,
        title: day
      }
    }
  }

  const { number, title } = getDayDisplay(day)

  return (
  <div className="relative w-full py-16 bg-gradient-to-r from-background via-accent/5 to-background">
    <div className="w-full px-8 md:px-16">
      <div className="flex items-center justify-between w-full">
        {/* Left side - Main content */}
        <div className="flex items-baseline gap-6">
          <div className="text-7xl md:text-9xl font-extralight text-primary/30 leading-none">
              {number}
          </div>
          <div>
              <h2 className="text-3xl md:text-5xl font-light text-primary tracking-wide uppercase">
                {title}
              </h2>
            <div className="text-sm md:text-base text-muted-foreground uppercase tracking-[0.4em] mt-1">
              {t('trips.summerTrip.itinerary.dayHeader.explore')} • {t('trips.summerTrip.itinerary.dayHeader.discover')} • {t('trips.summerTrip.itinerary.dayHeader.live')}
            </div>
          </div>
        </div>

        {/* Right side - Decorative line */}
        <div className="flex-1 ml-8 h-px bg-gradient-to-r from-border via-accent/30 to-transparent" />
      </div>
    </div>
  </div>
)
}

const MediaCarousel = ({
  media,
  sectionIndex,
  scrollY,
  onMediaClick,
}: { 
  media: DayItinerary["media"]; 
  sectionIndex: number; 
  scrollY: number;
  onMediaClick: (index: number) => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const getImageScale = (sectionIndex: number) => {
    const windowHeight = window.innerHeight
    const sectionStart = sectionIndex * windowHeight
    const sectionCenter = sectionStart + windowHeight / 2
    const currentScroll = scrollY + windowHeight / 2

    const distanceFromCenter = Math.abs(currentScroll - sectionCenter)
    const maxDistance = windowHeight / 2

    const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1)
    const scale = 1.3 - normalizedDistance * 0.5

    return Math.max(0.8, Math.min(1.3, scale))
  }


  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length)
  }

  const handleMediaClick = () => {
    onMediaClick(currentIndex)
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Media Container */}
      <div className="relative w-full h-full overflow-hidden">
        {media[currentIndex].type === "video" ? (
          <div 
            className="w-full h-full bg-gray-900 flex items-center justify-center cursor-pointer"
            onClick={handleMediaClick}
          >
            <div className="text-center text-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-white/30 hover:scale-110">
                <svg
                  className="w-8 h-8 transition-transform duration-300 hover:scale-110"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-200 bg-black/50 px-3 py-1 rounded-md">{media[currentIndex].alt}</p>
            </div>
          </div>
        ) : (
          <img
            src={media[currentIndex].previewSrc || media[currentIndex].src || "/trips/summer-trip.webp"}
            alt={media[currentIndex].alt}
            className="w-full h-full object-cover transition-all duration-500 ease-out cursor-pointer hover:scale-105"
            onClick={handleMediaClick}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/trips/summer-trip.webp";
            }}
          />
        )}

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const newIndex = (currentIndex - 1 + media.length) % media.length
                setCurrentIndex(newIndex)
                onMediaClick(newIndex)
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:shadow-lg z-10"
            >
              <svg
                className="w-5 h-5 transition-transform duration-200 hover:scale-110"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const newIndex = (currentIndex + 1) % media.length
                setCurrentIndex(newIndex)
                onMediaClick(newIndex)
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:shadow-lg z-10"
            >
              <svg
                className="w-5 h-5 transition-transform duration-200 hover:scale-110"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots Indicator - Hidden as requested */}
      </div>
    </div>
  )
}

// Function to generate dynamic image paths for destinations and activities
const generateDynamicImages = (type: 'destinations' | 'activities', id: string, count: number) => {
  const images: { type: "image"; src: string; alt: string; previewSrc?: string }[] = []
  
  // Special case for wild_life_spotting and deer_spotting - use available images
  if (id === 'wild_life_spotting' || id === 'deer_spotting') {
    images.push({
      type: "image",
      src: `/activities/wild_life_spotting/wild_life_spotting_banner.webp`,
      previewSrc: `/activities/wild_life_spotting/wild_life_spotting_banner.webp`,
      alt: `Wild Life Spotting - Banner`
    })
    return images
  }
  
  for (let i = 1; i <= count; i++) {
    const smallPath = `/${type}/${id}/${id}_${i}_small.webp`
    const mediumPath = `/${type}/${id}/${id}_${i}_medium.webp`
    
    images.push({
      type: "image",
      src: mediumPath, // Use medium for gallery
      previewSrc: smallPath, // Use small for preview
      alt: `${id} - ${i === 1 ? 'Panorama' : i === 2 ? 'Dettaglio' : i === 3 ? 'Vista' : `Immagine ${i}`}`
    })
  }
  
  return images
}

// Function to get image count for a destination/activity
const getImageCount = (type: 'destinations' | 'activities', id: string): number => {
  // Default counts based on known destinations/activities
  const imageCounts: Record<string, number> = {
    // Destinations
    'collalto_sabino': 16,
    'santo_stefano_di_sessanio': 50,
    'rocca_calascio': 25,
    'campo_imperatore': 39,
    'gran_sasso': 39, // Campo Imperatore is in Gran Sasso park
    'villetta_barrea': 30,
    'scanno': 4,
    'trabocchi_coast': 4,
    'torino_di_sangro': 10,
    'vasto': 8,
    'pescasseroli': 6,
    'pescocostanzo': 12,
    'punta_aderci': 10,
    'rocca_di_mezzo': 10,
    'san_vito_chietino': 12,
    'fossacesia': 6,
    'ortona': 8,
    'alba_fucens': 10,
    
    // Activities
    'sextantio': 25,
    'arrosticini_mucciante': 3,
    'cantina_tilli': 5,
    'wild_life_spotting': 1, // Special case - only has banner image
    'deer_spotting': 1 // Special case - uses wild_life_spotting banner
  }
  
  return imageCounts[id] || 3 // Default to 3 images if not found
}

// Function to convert trip data to day itinerary format
const convertTripDataToDayItinerary = (tripData: TripData, t: any): DayItinerary[] => {
  const dayItinerary: DayItinerary[] = []
  
  // Group days 4-6 into a single section
  const trabocchiDays = tripData.itinerary.filter(day => day.day >= 4 && day.day <= 6)
  const days1to3 = tripData.itinerary.filter(day => day.day >= 1 && day.day <= 3)
  const day7 = tripData.itinerary.filter(day => day.day === 7)
  
  // Add days 1-3 as individual destinations/activities/info
  days1to3.forEach((day: ItineraryDay) => {
    // Determine if this is an info card (no carousel needed)
    const isInfoCard = day.info && day.info.length > 0
    
    // Generate activities list based on card type
    let activities: string[] = []
    if (isInfoCard) {
      // For info cards, use the info array as activities
      activities = day.info || []
    } else if (day.activities && day.activities.length > 0) {
      // For activity cards, use predefined activities
      activities = [
        t('trips.summerTrip.itinerary.activities.arrival'),
        t('trips.summerTrip.itinerary.activities.guidedTour'), 
        t('trips.summerTrip.itinerary.activities.territoryExploration'),
        t('trips.summerTrip.itinerary.activities.lunchBreak'),
        t('trips.summerTrip.itinerary.activities.afternoonActivities'),
        t('trips.summerTrip.itinerary.activities.freeTime'),
        t('trips.summerTrip.itinerary.activities.traditionalDinner')
      ]
    } else {
      // For destination cards, use default activities
      activities = [
        t('trips.summerTrip.itinerary.activities.arrival'),
        t('trips.summerTrip.itinerary.activities.guidedTour'),
        t('trips.summerTrip.itinerary.activities.territoryExploration'), 
        t('trips.summerTrip.itinerary.activities.lunchBreak'),
        t('trips.summerTrip.itinerary.activities.afternoonActivities'),
        t('trips.summerTrip.itinerary.activities.freeTime'),
        t('trips.summerTrip.itinerary.activities.traditionalDinner')
      ]
    }
    
    // Generate dynamic images for destinations and activities
    let media: { type: "image"; src: string; alt: string }[] = []
    
    if (day.destinations && day.destinations.length > 0) {
      // For destinations, use dynamic images
      const destinationId = day.destinations[0]
      const imageCount = getImageCount('destinations', destinationId)
      media = generateDynamicImages('destinations', destinationId, imageCount)
    } else if (day.activities && day.activities.length > 0) {
      // For activities, use dynamic images
      const activityId = day.activities[0]
      const imageCount = getImageCount('activities', activityId)
      media = generateDynamicImages('activities', activityId, imageCount)
    } else if (day.images && day.images.length > 0) {
      // Fallback to provided images
      media = day.images.map((img, index) => ({
        type: "image" as const,
        src: img,
        alt: `${day.title} - ${index === 0 ? 'Panorama' : index === 1 ? 'Dettaglio' : 'Vista'}`
      }))
    } else {
      // Default fallback images using _banner.webp
      const fallbackImage = day.destinations && day.destinations.length > 0 
        ? `/destinations/${day.destinations[0]}/${day.destinations[0]}_banner.webp`
        : day.activities && day.activities.length > 0
        ? `/activities/${day.activities[0]}/${day.activities[0]}_banner.webp`
        : tripData.image
      
      media = [
        { type: "image", src: fallbackImage, alt: day.title },
        { type: "image", src: fallbackImage, alt: `${day.title} - Panorama` },
        { type: "image", src: fallbackImage, alt: `${day.location} - Vista` }
      ]
    }

    dayItinerary.push({
      day: day.day,
      title: day.title,
      location: day.location,
      description: day.description,
      destinations: day.destinations,
      activityLinks: day.activities,
      info: day.info,
      activities: activities,
      media: media,
      highlight: isInfoCard ? day.info?.[0] || day.title : t('trips.summerTrip.itinerary.highlights.discovery', { location: day.location })
    })
  })
  
  // Add grouped days 4-6 as a single section
  if (trabocchiDays.length > 0) {
    const trabocchiDestinations = trabocchiDays.find(day => day.destinations)?.destinations || []
    
    // Generate dynamic images for Trabocchi Coast
    let trabocchiMedia: { type: "image"; src: string; alt: string }[] = []
    if (trabocchiDestinations.length > 0) {
      const destinationId = trabocchiDestinations[0]
      const imageCount = getImageCount('destinations', destinationId)
      trabocchiMedia = generateDynamicImages('destinations', destinationId, imageCount)
    } else {
      // Fallback to default images using _banner.webp
      const fallbackImage = tripData.image
      trabocchiMedia = [
        { type: "image", src: fallbackImage, alt: "Costa dei Trabocchi - Trabocchi" },
        { type: "image", src: fallbackImage, alt: "Costa dei Trabocchi - Costa" },
        { type: "image", src: fallbackImage, alt: "Costa dei Trabocchi - Natura" }
      ]
    }
    
    dayItinerary.push({
      day: "4-6",
      title: t('trips.summerTrip.itinerary.titles.trabocchiCoast'),
      location: "Costa dei Trabocchi, Italia",
      description: t('trips.summerTrip.itinerary.descriptions.trabocchiCoast'),
      destinations: trabocchiDestinations,
      activityLinks: [],
      activities: [
        t('trips.summerTrip.itinerary.activities.arrivalAndSettlement'),
        t('trips.summerTrip.itinerary.activities.coastExploration'),
        t('trips.summerTrip.itinerary.activities.trabocchiVisit'),
        t('trips.summerTrip.itinerary.activities.traditionalGastronomy'),
        t('trips.summerTrip.itinerary.activities.relaxation'),
        t('trips.summerTrip.itinerary.activities.natureExcursions'),
        t('trips.summerTrip.itinerary.activities.localShopping'),
        t('trips.summerTrip.itinerary.activities.campingOvernights')
      ],
      media: trabocchiMedia,
      highlight: t('trips.summerTrip.itinerary.highlights.trabocchiExperience')
    })

    // Additional i18n-based info cards for the Trabocchi group
    dayItinerary.push({
      day: "4-6",
      title: t('trips.summerTrip.itinerary.extra.explore.title'),
      location: "Costa dei Trabocchi, Italia",
      description: t('trips.summerTrip.itinerary.extra.explore.text'),
      destinations: trabocchiDestinations,
      activityLinks: [],
      info: [t('trips.summerTrip.itinerary.extra.explore.text')],
      activities: [t('trips.summerTrip.itinerary.extra.explore.text')],
      media: [],
      highlight: t('trips.summerTrip.itinerary.extra.explore.title')
    })

    dayItinerary.push({
      day: "4-6",
      title: t('trips.summerTrip.itinerary.extra.relax.title'),
      location: "Costa dei Trabocchi, Italia",
      description: t('trips.summerTrip.itinerary.extra.relax.text'),
      destinations: trabocchiDestinations,
      activityLinks: [],
      info: [t('trips.summerTrip.itinerary.extra.relax.text')],
      activities: [t('trips.summerTrip.itinerary.extra.relax.text')],
      media: [],
      highlight: t('trips.summerTrip.itinerary.extra.relax.title')
    })
  }
  
  // Add day 7 as individual destinations/activities/info
  day7.forEach((day: ItineraryDay) => {
    // Determine if this is an info card (no carousel needed)
    const isInfoCard = day.info && day.info.length > 0
    
    // Generate activities list based on card type
    let activities: string[] = []
    if (isInfoCard) {
      // For info cards, use the info array as activities
      activities = day.info || []
    } else if (day.activities && day.activities.length > 0) {
      // For activity cards, use predefined activities
      activities = [
        t('trips.summerTrip.itinerary.activities.arrival'),
        t('trips.summerTrip.itinerary.activities.guidedTour'), 
        t('trips.summerTrip.itinerary.activities.territoryExploration'),
        t('trips.summerTrip.itinerary.activities.lunchBreak'),
        t('trips.summerTrip.itinerary.activities.afternoonActivities'),
        t('trips.summerTrip.itinerary.activities.freeTime'),
        t('trips.summerTrip.itinerary.activities.traditionalDinner')
      ]
    } else {
      // For destination cards, use default activities
      activities = [
        t('trips.summerTrip.itinerary.activities.arrival'),
        t('trips.summerTrip.itinerary.activities.guidedTour'),
        t('trips.summerTrip.itinerary.activities.territoryExploration'), 
        t('trips.summerTrip.itinerary.activities.lunchBreak'),
        t('trips.summerTrip.itinerary.activities.afternoonActivities'),
        t('trips.summerTrip.itinerary.activities.freeTime'),
        t('trips.summerTrip.itinerary.activities.traditionalDinner')
      ]
    }
    
    // Generate dynamic images for destinations and activities (day 7)
    let media: { type: "image"; src: string; alt: string }[] = []
    
    if (day.destinations && day.destinations.length > 0) {
      // For destinations, use dynamic images
      const destinationId = day.destinations[0]
      const imageCount = getImageCount('destinations', destinationId)
      media = generateDynamicImages('destinations', destinationId, imageCount)
    } else if (day.activities && day.activities.length > 0) {
      // For activities, use dynamic images
      const activityId = day.activities[0]
      const imageCount = getImageCount('activities', activityId)
      media = generateDynamicImages('activities', activityId, imageCount)
    } else if (day.images && day.images.length > 0) {
      // Fallback to provided images
      media = day.images.map((img, index) => ({
        type: "image" as const,
        src: img,
        alt: `${day.title} - ${index === 0 ? 'Panorama' : index === 1 ? 'Dettaglio' : 'Vista'}`
      }))
    } else {
      // Default fallback images using _banner.webp
      const fallbackImage = day.destinations && day.destinations.length > 0 
        ? `/destinations/${day.destinations[0]}/${day.destinations[0]}_banner.webp`
        : day.activities && day.activities.length > 0
        ? `/activities/${day.activities[0]}/${day.activities[0]}_banner.webp`
        : tripData.image
      
      media = [
        { type: "image", src: fallbackImage, alt: day.title },
        { type: "image", src: fallbackImage, alt: `${day.title} - Panorama` },
        { type: "image", src: fallbackImage, alt: `${day.location} - Vista` }
      ]
    }
    
    dayItinerary.push({
      day: day.day,
      title: day.title,
      location: day.location,
      description: day.description,
      destinations: day.destinations,
      activityLinks: day.activities,
      info: day.info,
      activities: activities,
      media: media,
      highlight: isInfoCard ? day.info?.[0] || day.title : t('trips.summerTrip.itinerary.highlights.discovery', { location: day.location })
    })
  })
  
  return dayItinerary
}

// Fullscreen Media Modal Component
const FullscreenMediaModal = ({
  isOpen,
  onClose,
  media,
  currentIndex,
  onPrevious,
  onNext,
  t
}: {
  isOpen: boolean
  onClose: () => void
  media: DayItinerary["media"]
  currentIndex: number
  onPrevious: () => void
  onNext: () => void
  t: (key: string) => string
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return
      
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft') {
        onPrevious()
      } else if (e.key === 'ArrowRight') {
        onNext()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, onPrevious, onNext])

  if (!isOpen || !media[currentIndex]) return null

  const currentMedia = media[currentIndex]

  // Ensure we're in the browser before rendering the portal
  if (typeof window === 'undefined') return null

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center" 
      onClick={onClose} 
      role="dialog" 
      aria-modal="true" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 9999,
        width: '100vw',
        height: '100vh'
      }}
    >
      {/* Close Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 z-50 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
        aria-label={t('itinerary.closeMediaViewer')}
        type="button"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Media Container */}
      <div 
        className="relative w-full h-full flex items-center justify-center p-8" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          width: '100%', 
          height: '100%',
          maxWidth: '100vw',
          maxHeight: '100vh'
        }}
      >
        {currentMedia.type === "video" ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-32 h-32 mx-auto mb-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-xl text-gray-200 bg-black/50 px-6 py-3 rounded-lg">{currentMedia.alt}</p>
            </div>
          </div>
        ) : (
          <img
            src={currentMedia.src || "/trips/summer-trip.webp"}
            alt={currentMedia.alt}
            className="max-w-full max-h-full object-contain"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/trips/summer-trip.webp";
            }}
          />
        )}

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onPrevious(); }}
              className="absolute left-8 top-1/2 transform -translate-y-1/2 w-16 h-16 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
              type="button"
              aria-label={t('itinerary.previousMedia')}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 w-16 h-16 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110"
              type="button"
              aria-label={t('itinerary.nextMedia')}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Media Counter */}
        {media.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg">
            {currentIndex + 1} / {media.length}
          </div>
        )}

        {/* Media Title */}
        <div className="absolute top-8 left-8 bg-black/50 text-white px-4 py-2 rounded-lg">
          {currentMedia.alt}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default function TravelItinerary({ tripData }: TravelItineraryProps) {
  const { t } = useLanguage()
  const [scrollY, setScrollY] = useState(0)
  const [activeSection, setActiveSection] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMedia, setModalMedia] = useState<DayItinerary["media"]>([])
  const [modalCurrentIndex, setModalCurrentIndex] = useState(0)
  const [scrollableCards, setScrollableCards] = useState<Set<string>>(new Set())
  const [expandedCard, setExpandedCard] = useState<{day: number | string, dayIndex: number, title: string, description: string} | null>(null)

  // Function to check if content is scrollable
  const checkScrollable = (elementId: string) => {
    const element = document.getElementById(elementId)
    if (element) {
      const isScrollable = element.scrollHeight > element.clientHeight
      setScrollableCards(prev => {
        const newSet = new Set(prev)
        if (isScrollable) {
          newSet.add(elementId)
        } else {
          newSet.delete(elementId)
        }
        return newSet
      })
    }
  }

  // Scroll indicator component
  const ScrollIndicator = ({ cardId }: { cardId: string }) => {
    const [isVisible, setIsVisible] = useState(true)
    const isScrollable = scrollableCards.has(cardId)
    
    useEffect(() => {
      if (!isScrollable) return
      
      const cardElement = document.getElementById(cardId)
      if (!cardElement) return
      
      const handleScroll = () => {
        setIsVisible(false)
      }
      
      cardElement.addEventListener('scroll', handleScroll)
      return () => cardElement.removeEventListener('scroll', handleScroll)
    }, [cardId, isScrollable])
    
    if (!isScrollable || !isVisible) return null
    
    return (
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1 animate-bounce">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
        <span>{t('itinerary.scrollToContinue')}</span>
      </div>
    )
  }

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)

      const windowHeight = window.innerHeight
      const scrollPosition = window.scrollY + windowHeight / 2
      const itinerary = convertTripDataToDayItinerary(tripData, t)
      const totalSections = itinerary.length + itinerary.length // giorni + header giorni
      const sectionHeight = windowHeight
      const newActiveSection = Math.min(Math.max(0, Math.floor(scrollPosition / sectionHeight)), totalSections - 1)
      setActiveSection(newActiveSection)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [tripData, t])

  // Check if cards are scrollable when component mounts and on resize
  useEffect(() => {
    const checkAllCards = () => {
      const itinerary = convertTripDataToDayItinerary(tripData, t)
      itinerary.forEach((day, dayIndex) => {
        const cardId = `card-${day.day}-${dayIndex}`
        setTimeout(() => checkScrollable(cardId), 100) // Small delay to ensure DOM is ready
      })
    }

    checkAllCards()
    window.addEventListener('resize', checkAllCards)
    
    return () => window.removeEventListener('resize', checkAllCards)
  }, [tripData, t])

  const itinerary = convertTripDataToDayItinerary(tripData, t)

  // Modal handlers
  const handleMediaClick = (media: DayItinerary["media"], index: number) => {
    setModalMedia(media)
    setModalCurrentIndex(index)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const goToPreviousMedia = () => {
    setModalCurrentIndex((prev) => (prev - 1 + modalMedia.length) % modalMedia.length)
  }

  const goToNextMedia = () => {
    setModalCurrentIndex((prev) => (prev + 1) % modalMedia.length)
  }

  let sectionIndex = 0

  return (
    <div className="relative bg-background">

      {/* Itinerary */}
      {itinerary.map((day, dayIndex) => {
        const currentSectionIndex = sectionIndex++
        const isFirstOfDay = dayIndex === 0 || itinerary[dayIndex - 1].day !== day.day
        const isInfoCard = day.info && day.info.length > 0
        
        return (
          <div key={`${day.day}-${dayIndex}`}>
            {isFirstOfDay && (
              <div id={`day-header-${day.day}`} className="relative">
                <DayHeader day={day.day} />
              </div>
            )}

            <section
              key={`${day.day}-${dayIndex}`}
              id={`day-${day.day}-${dayIndex}`}
              className={`relative ${isInfoCard ? 'h-activity-card' : 'h-[60vh] md:h-[90vh]'} overflow-hidden`}
            >
              {/* Carousel Section - Top 1/2 (only for non-info cards) */}
              {!isInfoCard && (
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gray-900">
                  <div className="relative w-full h-full">
                    <MediaCarousel 
                      media={day.media} 
                      sectionIndex={currentSectionIndex} 
                      scrollY={scrollY}
                      onMediaClick={(index) => handleMediaClick(day.media, index)}
                    />
                  </div>
                </div>
              )}

              {/* Card Section - Bottom 1/2 + Overlap (or reduced height for info cards) */}
              <div className={`${day.day === 7 ? 'relative' : 'absolute'} ${isInfoCard ? '' : 'top-1/3'} left-0 w-full ${isInfoCard ? 'h-2/3' : 'h-2/3'} flex justify-center items-start`}>
                <div className="w-full max-w-6xl px-4 md:px-8 pb-0 -mb-8">
                  <div 
                    id={`card-${day.day}-${dayIndex}`}
                    className={`bg-white rounded-2xl shadow-2xl border border-gray-200 px-4 md:px-8 pt-6 md:pt-8 pb-6 md:pb-8 max-w-full overflow-y-auto mb-0 relative ${isInfoCard ? 'h-full max-h-[35vh] flex flex-col justify-start' : 'max-h-[30vh]'}`}
                    style={{
                      transform: `scale(${1 + Math.sin(scrollY * 0.01) * 0.05})`,
                      transition: 'transform 0.3s ease-out'
                    }}
                  >
                    <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-balance mb-4 text-center break-words">
                      {(() => {
                        // Exclude specific titles from having links
                        if (day.title === 'Pernottamento a Opi') {
                          return day.title
                        }
                        
                        const destinationUrl = getDestinationUrl(day.location, day.destinations)
                        const activityUrl = day.activityLinks && day.activityLinks.length > 0 
                          ? getActivityUrl(day.title) 
                          : null
                        
                        // Priority: activity link > destination link > no link
                        const linkUrl = activityUrl || destinationUrl
                        
                        return linkUrl ? (
                          <Link 
                            to={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-blue-600 transition-colors duration-200 cursor-pointer hover:scale-105 transform inline-block"
                          >
                            {day.title}
                          </Link>
                        ) : (
                          day.title
                        )
                      })()}
                    </h3>

                    <div 
                      className="text-lg md:text-xl text-gray-600 leading-relaxed text-pretty mb-6 text-center break-words"
                      dangerouslySetInnerHTML={{ __html: day.description }}
                    />

                    <ScrollIndicator cardId={`card-${day.day}-${dayIndex}`} />
                    
                    {/* Expand button */}
                    <button
                      onClick={() => setExpandedCard({
                        day: day.day,
                        dayIndex,
                        title: day.title,
                        description: day.description
                      })}
                      className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 animate-pulse"
                      title={t('itinerary.expand')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )
      })}

      {/* Fullscreen Media Modal */}
      <FullscreenMediaModal
        isOpen={isModalOpen}
        onClose={closeModal}
        media={modalMedia}
        currentIndex={modalCurrentIndex}
        onPrevious={goToPreviousMedia}
        onNext={goToNextMedia}
        t={t}
      />

      {/* Expanded Text Modal */}
      {expandedCard && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setExpandedCard(null)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                {expandedCard.title}
              </h2>
              <button
                onClick={() => setExpandedCard(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div 
                className="text-lg text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: expandedCard.description }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Creative Commons Credits - Only for summer-trip */}
      {tripData.id === 'cmefxo59l0000n09als3vgwn1' && (
        <div className="bg-white rounded-lg p-6 shadow-sm mx-4 mb-4">
          <p className="text-left text-xs text-gray-500">
            <span>{t('landing.footer.creativeCommons')}</span>
            <a 
              href="/commons.wikimedia.info" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              {t('landing.footer.fullCredits')}
            </a>
          </p>
        </div>
      )}

      {/* PDF Download and Contact Information Section - Only for summer-trip */}
      {tripData.id === 'cmefxo59l0000n09als3vgwn1' && (
        <div className="bg-white rounded-lg p-6 shadow-sm mx-4 mb-4">
          <p className="text-left text-sm text-gray-700">
            {t('itinerary.pdfDownload')}{' '}
            <a 
              href="/trips/summer-trip-en.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline font-medium"
            >
              {t('itinerary.pdfEnglish')}
            </a>
            {' '}{t('itinerary.pdfAnd')}{' '}
            <a 
              href="/trips/summer-trip-nl.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline font-medium"
            >
              {t('itinerary.pdfDutch')}
            </a>
            . {t('itinerary.contactInfo')}{' '}
            <a 
              href="/contact" 
              className="text-blue-500 hover:text-blue-600 underline font-medium"
            >
              {t('itinerary.contactForm')}
            </a>
            {' '}{t('itinerary.contactEmail')}{' '}
            <a 
              href={`mailto:${t('itinerary.contactEmailAddress')}`}
              className="text-blue-500 hover:text-blue-600 underline font-medium"
            >
              {t('itinerary.contactEmailAddress')}
            </a>
            .
          </p>
        </div>
      )}

    </div>
  )
}
